import React from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { motion, AnimatePresence } from 'framer-motion';
import { Play, Pause, X } from 'lucide-react';
import { useAudioStore } from '@/hooks/useAudioStore';
import { useAudioPlayer } from '@/hooks/useAudioPlayer';
import { getStreamColor, getSolidColor } from '@/utils/streamColor';
import { getLocalizedStationName } from '@/utils/stationName';
import { maybeTransliterate } from '@/utils/transliterate';
import { MarqueeText } from '@/components/MarqueeText';

/** Mini player – prikazuje se kad postoji currentStation, iznad navigacije */
export const MiniPlayer: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { t, i18n } = useTranslation();
  const locale = i18n.resolvedLanguage || i18n.language || 'de';
  const { currentStation, currentTrack, isPlaying, setCurrentStation } = useAudioStore();
  const { togglePlayPause, stop } = useAudioPlayer();

  const handleClose = (e: React.MouseEvent) => {
    e.stopPropagation();
    stop();
    setCurrentStation(null);
  };

  const isPlayerScreen = location.pathname === '/app/player';
  const show = currentStation && !isPlayerScreen;

  if (!show) return null;

  const color = getStreamColor(currentStation?.color, currentStation?.name) ?? '#3b82f6';
  const solidColor = getSolidColor(color);

  return (
    <AnimatePresence>
      <motion.div
        initial={{ y: 80, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        exit={{ y: 80, opacity: 0 }}
        transition={{ type: 'spring', damping: 25, stiffness: 300 }}
        onClick={() => navigate('/app/player')}
        className="fixed left-4 right-4 z-[60] mx-auto max-w-lg cursor-pointer"
        style={{
          bottom: 'calc(env(safe-area-inset-bottom, 0px) + 4rem)',
        }}
      >
        <div className="glass rounded-2xl p-3 flex items-center gap-3 shadow-lg border border-white/10 hover:bg-white/5 transition-colors">
          {/* Artwork */}
          <div className="w-12 h-12 rounded-xl overflow-hidden flex-shrink-0">
            <img
              src={currentStation?.artwork}
              alt={currentStation?.name ?? ''}
              className="w-full h-full object-cover"
            />
          </div>

          {/* Info */}
          <div className="flex-1 min-w-0">
            <p className="font-semibold truncate text-sm" style={{ color: solidColor }}>
              {currentStation ? getLocalizedStationName(currentStation, locale) : ''}
            </p>
            <p className="text-xs text-text-muted">
              {!isPlaying ? (
                <span className="truncate block">{t('miniPlayer.paused')}</span>
              ) : currentTrack ? (
                <MarqueeText
                  className="text-xs text-text-muted uppercase"
                  text={`${maybeTransliterate(currentTrack.artist, locale)}${currentTrack.artist ? ' - ' : ''}${maybeTransliterate(currentTrack.title, locale)}`}
                />
              ) : (
                <span className="truncate block">{t('miniPlayer.listening')}</span>
              )}
            </p>
          </div>

          {/* Play/Pause */}
          <button
            onClick={(e) => {
              e.stopPropagation();
              togglePlayPause();
            }}
            className="w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0 transition-transform hover:scale-105 active:scale-95"
            style={{
              background: color,
              boxShadow: `0 4px 20px ${solidColor}50`,
            }}
          >
            {isPlaying ? (
              <Pause className="w-5 h-5 fill-current text-white" />
            ) : (
              <Play className="w-5 h-5 fill-current text-white ml-0.5" />
            )}
          </button>

          {/* Zatvori */}
          <button
            onClick={handleClose}
            aria-label="Zatvori player"
            className="w-9 h-9 rounded-full flex items-center justify-center flex-shrink-0 text-text-muted hover:bg-white/10 hover:text-white transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>
      </motion.div>
    </AnimatePresence>
  );
};
